import { environment } from 'src/environments/environment';
import { z } from 'zod';
import { Api } from '../api';
import { GenerateTokenData, GenerateTokenSchema } from './schemas/generate-token';
import { HttpService } from '../../services/http/http.service';
import { ApiResponseSchema } from '../api-response';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { BootupData, BootupSchema } from './schemas/bootup';
import { NullDataSchema } from './schemas/null-data';

export const SPARKET_BRIDGE_ENDPOINTS = {
  BOOTUP: 'bootup',
  CREATE_TOKEN: 'create-token',
  PREPARE_DEPOSIT_FUNDS: 'prepare-deposit-funds',
} as const;

export class SparketBridgeApi {
  private api: Api;

  private constructor(httpService: HttpService, analyticsService: AnalyticsService) {
    this.api = new Api(environment.sparketBridgeHost, httpService, analyticsService);
  }

  public static request(httpService: HttpService, analyticsService: AnalyticsService) {
    return new SparketBridgeApi(httpService, analyticsService);
  }

  public async bootup(): Promise<BootupData> {
    const BootupResponseSchema = ApiResponseSchema(BootupSchema);
    type BootupResponse = z.infer<typeof BootupResponseSchema>;

    const response = await this.api.get<BootupResponse>(
      SPARKET_BRIDGE_ENDPOINTS.BOOTUP,
      BootupResponseSchema,
    );
    return response.data;
  }

  public async generateToken(displayName: string): Promise<GenerateTokenData> {
    const GenerateTokenResponseSchema = ApiResponseSchema(GenerateTokenSchema);
    type GenerateTokenResponse = z.infer<typeof GenerateTokenResponseSchema>;

    const response = await this.api.post<GenerateTokenResponse, { displayName: string }>(
      SPARKET_BRIDGE_ENDPOINTS.CREATE_TOKEN,
      GenerateTokenResponseSchema,
      { displayName },
    );
    return response.data;
  }

  public async prepareDepositFunds(amount: number, gameId: string): Promise<void> {
    const NullDataResponseSchema = ApiResponseSchema(NullDataSchema);
    type NullDataResponse = z.infer<typeof NullDataResponseSchema>;

    await this.api.post<NullDataResponse, { amount: number; gameId: string }>(
      SPARKET_BRIDGE_ENDPOINTS.PREPARE_DEPOSIT_FUNDS,
      NullDataResponseSchema,
      {
        amount,
        gameId,
      },
    );
  }
}
